import React from 'react';
import dayjs from 'dayjs';
import useWindowDimensions from 'shared/hooks/useWindowDimensions';

const LogDetailCard = ({ data }) => {
  const tariffBook = data?.data?.tariff_book;
  const ocrLogs = data?.data?.ocr_logs;

  return (
    <>
      <LogSummarySection data={tariffBook} />
      {ocrLogs?.length > 0 ? (
        <>
          <LogSectionHeader title="OCR" />
          <LogContentSection logs={ocrLogs} />
        </>
      ) : (
        <LogEmptyState />
      )}
    </>
  );
};

function LogSummarySection({ data }) {
  return (
    <section
      aria-label="Tariff book summary"
      className="w-full bg-white border rounded-lg flex flex-wrap p-4 my-3"
    >
      <InfoField label="ID" value={data?.id ?? '-'} />
      <InfoField
        label="Request Date"
        value={
          data?.created_at
            ? dayjs(data?.created_at).format('DD MMMM YYYY')
            : '-'
        }
      />
      <InfoField label="Name" value={data?.organization?.name ?? '-'} />
    </section>
  );
}

function InfoField({ label, value }) {
  return (
    <div className="w-full md:w-1/3 flex md:flex-col flex-wrap justify-between">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="text-sm text-slate-900">{value}</span>
    </div>
  );
}

function LogSectionHeader({ title }) {
  return (
    <nav
      aria-label="Log sections"
      className="flex gap-1 pb-2 mb-2 w-full overflow-x-auto whitespace-nowrap my-2"
    >
      <h2
        className="flex gap-3 items-center text-base p-2 font-medium text-blue-500 border-b-2 border-primary-500"
        aria-current="true"
      >
        {title}
      </h2>
    </nav>
  );
}

function LogContentSection({ logs }) {
  const { isMonitor } = useWindowDimensions();

  return (
    <section
      aria-label="OCR log entries"
      className={`w-full grid grid-cols-2 gap-4 overflow-auto ${isMonitor ? 'md:min-h-[550px] md:max-h-[650px]' : 'md:min-h-[400px] md:max-h-[450px]'}`}
    >
      {logs?.map((log, index) => (
        <React.Fragment key={log?.id ?? index}>
          <LogPayloadCard log={log} isMonitor={isMonitor} />
          <LogResultCard log={log} isMonitor={isMonitor} />
        </React.Fragment>
      ))}
    </section>
  );
}

function LogPayloadCard({ log, isMonitor }) {
  return (
    <article className="w-full bg-white border rounded-lg flex flex-wrap py-2 my-3">
      <div className="w-full px-4 pt-1 pb-3 flex flex-wrap items-center">
        <h3 className="w-full text-xl font-semibold">Payload</h3>
        <div
          className={`w-full bg-slate-100 text-sm border rounded-lg p-5 mt-5 ${isMonitor ? 'md:min-h-[550px] md:max-h-[400px]' : 'md:min-h-[330px] md:max-h-[300px]'} overflow-auto`}
        >
          {log?.payload || 'No payload available'}
        </div>
      </div>
    </article>
  );
}

function LogResultCard({ log, isMonitor }) {
  return (
    <article className="w-full bg-white border rounded-lg flex flex-wrap py-2 my-3">
      <div className="w-full px-4 pt-1 pb-3 flex flex-wrap items-center">
        <div className="w-full flex items-center gap-2 overflow-x-auto whitespace-nowrap">
          <h3 className="text-xl font-semibold">{log?.label}</h3>
          <span className="text-sm font-normal">{`| ${log?.model} | ${log?.variant}`}</span>
        </div>
        <div
          className={`w-full border bg-slate-100 text-sm rounded-lg p-5 mt-5 ${isMonitor ? 'md:min-h-[550px] md:max-h-[400px]' : 'md:min-h-[330px] md:max-h-[300px]'} overflow-auto`}
        >
          {log?.result || 'No result available'}
        </div>
      </div>
    </article>
  );
}

function LogEmptyState() {
  return (
    <div className="w-full rounded-lg p-6 text-base text-slate-500 flex justify-center items-center border border-slate-200 bg-white my-3 min-h-[300px]">
      Payload Empty!
    </div>
  );
}

export default LogDetailCard;
